import { NextResponse } from 'next/server';

import { API_BASE_URL, API_TIMEOUT_MS } from '@/constants';
import type { ApiRequestOptions, ApiResponseOptions, ApiResult } from '@/types';
import { getToken } from '@/utils/token';

export const logger = {
    error: (message: string, data?: unknown) => {
        console.error(`[ERROR] ${message}`, data);
    },
    info: (message: string, data?: unknown) => {
        console.info(`[INFO] ${message}`, data);
    },
};

function buildUrl(path: string, queryParams?: Record<string, string>): URL {
    const url = new URL(`${API_BASE_URL}${path}`);
    if (queryParams) {
        Object.entries(queryParams).forEach(([key, value]) => {
            if (value !== null && value !== undefined) {
                url.searchParams.append(key, value);
            }
        });
    }
    return url;
}

async function buildDefaultHeaders(body: FormData | object | undefined): Promise<Record<string, string>> {
    const token = await getToken();

    const headers: Record<string, string> = {
        Accept: 'application/json',
        'ocp-apim-subscription-key': process.env.MERCHANT_UTIL_APIM_API_TOKEN as string,
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        Pragma: 'no-cache',
    };

    // ----- << Pass `Authorization` if `token` is there >> -----
    if (token) {
        headers.Authorization = `Bearer ${token}`;
    }

    // ----- << Content-Type header for everything except FormData >> -----
    if (!(body instanceof FormData)) {
        headers['Content-Type'] = 'application/json';
    }

    return headers;
}

function buildFetchOptions(
    method: string,
    defaultHeaders: Record<string, string>,
    customHeaders: Record<string, string>,
    timeoutMs: number,
    body?: FormData | object
): RequestInit {
    const options: RequestInit = {
        method,
        headers: {
            ...defaultHeaders,
            ...customHeaders,
        },
        signal: AbortSignal.timeout(timeoutMs),
    };

    if (body) {
        options.body = body instanceof FormData ? body : JSON.stringify(body);
    }

    return options;
}

async function handleErrorResponse(response: Response, logPrefix: string, logParams: Record<string, unknown>) {
    let errorContent;

    const responseBodyText = await response.text();

    try {
        errorContent = JSON.parse(responseBodyText);
    } catch (parseError) {
        errorContent = responseBodyText;
    }

    logger.error(`${logPrefix} failed`, {
        status: response.status,
        statusText: response.statusText,
        url: response.url,
        headers: Object.fromEntries(response.headers.entries()),
        responseText: typeof errorContent === 'string' ? errorContent.substring(0, 1000) : errorContent,
        ...logParams,
    });

    return {
        success: false,
        data: null,
        error: errorContent || `${logPrefix} failed`,
        status: response.status,
    };
}

async function handleResponse(response: Response, logPrefix: string, logParams: Record<string, unknown>) {
    if (!response.ok) {
        return await handleErrorResponse(response, logPrefix, logParams);
    }

    const data = await response.json();
    return { success: true, data, error: null, status: response.status };
}

export async function executeApiRequest<_T>(
    {
        method = 'GET',
        path,
        body,
        queryParams,
        headers: customHeaders = {},
        timeoutMs = API_TIMEOUT_MS,
    }: ApiRequestOptions,
    { logPrefix, logParams = {} }: ApiResponseOptions
) {
    const url = buildUrl(path, queryParams);
    const defaultHeaders = await buildDefaultHeaders(body);
    const fetchOptions = buildFetchOptions(method, defaultHeaders, customHeaders, timeoutMs, body);
    try {
        const response = await fetch(url.toString(), fetchOptions);

        return await handleResponse(response, logPrefix, logParams);
    } catch (error) {
        logger.error(`${logPrefix} error`, error);
        return {
            success: false,
            data: null,
            error: String(error),
            status: 500,
        };
    }
}

export function createNextResponse(result: ApiResult) {
    return NextResponse.json(
        {
            success: result.success,
            ...(result.success ? { data: result.data, error: null } : { data: null, error: result.error }),
        },
        { status: result.status }
    );
}
